/**
 * Codes d'erreur stables du parcours IA (spec « utilisable par les IA » v2,
 * §3.2). Partagés par le serveur MCP (`isError: true` + code) et par le
 * parseur du lien pré-rempli `/new` (avertissements).
 *
 * ⚠️ Ces chaînes sont un CONTRAT public lu par des IA : ne jamais renommer un
 * code existant, seulement en ajouter.
 */
import {
  AI_POLL_DEFAULT_LANGUAGE,
  translateAiPoll,
  type AiPollLanguage,
  type MessageParams,
} from "./messages.js";

export const AI_POLL_ERROR_CODES = [
  "INVALID_FORMAT",
  "DATE_IN_PAST",
  "DATE_TOO_FAR",
  "TOO_MANY_OPTIONS",
  "TITLE_INVALID",
  "NOT_OWNER",
  "ACCOUNT_REQUIRED",
  "POLL_LIMIT_REACHED",
  "SOLO_REQUIRED",
  "POLL_NOT_FOUND",
  "POLL_CLOSED",
  "RATE_LIMITED",
  "CREATION_DISABLED",
  "INTERNAL",
] as const;

export type AiPollErrorCode = (typeof AI_POLL_ERROR_CODES)[number];

/**
 * Codes propres aux avertissements (jamais bloquants). Les avertissements
 * réutilisent aussi les codes d'erreur ci-dessus quand le problème est le même
 * (ex. `DATE_IN_PAST` : erreur en mode strict, avertissement en mode souple).
 */
export const AI_POLL_WARNING_ONLY_CODES = [
  "SLOTS_OVERLAP",
  "LANGUAGE_UNSUPPORTED",
] as const;

export type AiPollWarningOnlyCode = (typeof AI_POLL_WARNING_ONLY_CODES)[number];
export type AiPollIssueCode = AiPollErrorCode | AiPollWarningOnlyCode;

/** Clé de message par défaut de chaque code (namespace `ai_poll`). */
export const AI_POLL_DEFAULT_MESSAGE_KEY: Record<AiPollErrorCode, string> = {
  INVALID_FORMAT: "errors.invalid_format",
  DATE_IN_PAST: "errors.date_in_past",
  DATE_TOO_FAR: "errors.date_too_far",
  TOO_MANY_OPTIONS: "errors.too_many_dates",
  TITLE_INVALID: "errors.title_invalid",
  NOT_OWNER: "errors.not_owner",
  ACCOUNT_REQUIRED: "errors.account_required",
  POLL_LIMIT_REACHED: "errors.poll_limit_reached",
  SOLO_REQUIRED: "errors.solo_required",
  POLL_NOT_FOUND: "errors.poll_not_found",
  POLL_CLOSED: "errors.poll_closed",
  RATE_LIMITED: "errors.rate_limited",
  CREATION_DISABLED: "errors.creation_disabled",
  INTERNAL: "errors.internal",
};

export interface AiPollIssue<C extends string = AiPollIssueCode> {
  code: C;
  /** Texte rendu dans `language` (anglais par défaut = texte canonique). */
  message: string;
  /** Clé i18n (namespace `ai_poll`) : le front rend `t("ai_poll:" + messageKey, params)`. */
  messageKey: string;
  params: MessageParams;
  details: Record<string, unknown>;
}

export type AiPollError = AiPollIssue<AiPollErrorCode>;

export function isAiPollErrorCode(value: unknown): value is AiPollErrorCode {
  return (
    typeof value === "string" &&
    (AI_POLL_ERROR_CODES as readonly string[]).includes(value)
  );
}

export interface IssueOptions {
  /** Variante de message (ex. `errors.too_many_slots` pour TOO_MANY_OPTIONS). */
  messageKey?: string;
  params?: MessageParams;
  details?: Record<string, unknown>;
  language?: AiPollLanguage;
}

/** Construit `{code, message, messageKey, params, details}` pour un code d'erreur. */
export function aiPollError(
  code: AiPollErrorCode,
  options: IssueOptions = {},
): AiPollError {
  return makeIssue(
    code,
    options.messageKey ?? AI_POLL_DEFAULT_MESSAGE_KEY[code],
    options,
  );
}

/** Même forme, pour un avertissement (code d'erreur ou code propre). */
export function aiPollIssue<C extends AiPollIssueCode>(
  code: C,
  messageKey: string,
  options: Omit<IssueOptions, "messageKey"> = {},
): AiPollIssue<C> {
  return makeIssue(code, messageKey, options);
}

function makeIssue<C extends string>(
  code: C,
  messageKey: string,
  options: Omit<IssueOptions, "messageKey">,
): AiPollIssue<C> {
  const params = options.params ?? {};
  return {
    code,
    message: translateAiPoll(
      messageKey,
      options.language ?? AI_POLL_DEFAULT_LANGUAGE,
      params,
    ),
    messageKey,
    params,
    details: options.details ?? {},
  };
}

/** Re-rend un problème dans une autre langue (ex. langue du sondage). */
export function localizeIssue<C extends string>(
  issue: AiPollIssue<C>,
  language: AiPollLanguage,
): AiPollIssue<C> {
  return {
    ...issue,
    message: translateAiPoll(issue.messageKey, language, issue.params),
  };
}
